import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";
import participantsPaneTheme from "../../../base/components/themes/participantsPaneTheme.json";
import { openDialog } from "../../../base/dialog/actions";
import { getRoleParticipant, isLocalParticipantModerator } from "../../../base/participants/functions";
import Button from "../../../base/ui/components/web/Button";
import { BUTTON_TYPES } from "../../../base/ui/constants.web";
import { findAncestorByClass } from "../../../base/ui/functions.web";
import { getBreakoutRooms, getCurrentRoomId, isAddBreakoutRoomButtonVisible } from "../../../breakout-rooms/functions";
import MuteEveryoneDialog from "../../../video-menu/components/web/MuteEveryoneDialog";
import { changeTabParticipant, close } from "../../actions.web";
import { getParticipantsPaneOpen, getSortedParticipantIds, isMoreActionsVisible, isMuteAllVisible, } from "../../functions";
import { createToolbarEvent } from "../../../analytics/AnalyticsEvents";
import { sendAnalytics } from "../../../analytics/functions";
import { beginAddPeople } from "../../../invite/actions.any";
import { getKnockingParticipants } from "../../../lobby/functions";
import HeaderMenuController from "../../../moderator-controller/components/HeaderMenuController";
import SideBar from "../../../moderator-controller/components/SideBar";
import MuteEveryonesVideoDialog from "../../../video-menu/components/web/MuteEveryonesVideoDialog";
import LobbyParticipants from "./LobbyParticipants";
import MeetingParticipants from "./MeetingParticipants";
import VisitorsList from "./VisitorsList";
import Attendance from "./Attendance";
import apiManagerRoom from "../../../api/apiManagerRoom";
import RaisedHandParticipants from "./RaisedHandParticipants";
import { ROLE_PARTICIPANT_ROOM } from "../../../base/constants/default";
const useStyles = makeStyles()((theme) => {
    return {
        participantsPane: {
            backgroundColor: theme.palette.ui01,
            flexShrink: 0,
            overflow: "hidden",
            position: "relative",
            transition: "width .16s ease-in-out",
            width: "315px",
            zIndex: 0,
            display: "flex",
            flexDirection: "column",
            fontWeight: 600,
            height: "100%",
            [["& > *:first-of-type", "& > *:last-child"]]: {
                flexShrink: 0,
            },
            "@media (max-width: 580px)": {
                height: "100dvh",
                position: "fixed",
                left: 0,
                right: 0,
                top: 0,
                width: "100%",
            },
        },
        container: {
            boxSizing: "border-box",
            flex: 1,
            // overflowY: 'auto',
            position: "relative",
            padding: `0 11px 0 16px`,
            marginRight: '5px',
            height: 'calc(100vh - 330px)',
            overflowY: 'auto',
            "&::-webkit-scrollbar": {
            // display: "none",
            },
        },
        containerNoFooter: {
            height: 'calc(100vh - 260px)',
        },
        closeButton: {
            alignItems: "center",
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
        },
        header: {
            alignItems: "center",
            boxSizing: "border-box",
            display: "flex",
            height: "60px",
            padding: `0 ${participantsPaneTheme.panePadding}px`,
            justifyContent: "space-between",
        },
        antiCollapse: {
            fontSize: 0,
            "&:first-of-type": {
                display: "none",
            },
            "&:first-of-type + *": {
                marginTop: 0,
            },
        },
        footer: {
            // marginRight: '22px',
            fontSize: "14px !important",
            position: "absolute",
            bottom: 0,
            display: "flex",
            justifyContent: "flex-end",
            padding: `${theme.spacing(4)} ${participantsPaneTheme.panePaddingCustom}px`,
            "& > *:not(:last-child)": {
                marginRight: theme.spacing(3),
            },
        },
        footerMoreContainer: {
            position: "relative",
        },
        textSizeCustom: {
            fontSize: "14px",
        },
    };
});
const ParticipantsPane = () => {
    const { classes, cx } = useStyles();
    const participants = useSelector(getKnockingParticipants);
    const paneOpen = useSelector(getParticipantsPaneOpen);
    const isBreakoutRoomsSupported = useSelector((state) => state["features/base/conference"])
        .conference?.getBreakoutRooms()
        ?.isSupported();
    const showAddRoomButton = useSelector(isAddBreakoutRoomButtonVisible);
    const showFooter = useSelector(isLocalParticipantModerator);
    const showMuteAllButton = useSelector(isMuteAllVisible);
    const showMoreActionsButton = useSelector(isMoreActionsVisible);
    let sortedParticipantIds = useSelector(getSortedParticipantIds);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [contextOpen, setContextOpen] = useState(false);
    const [searchString, setSearchString] = useState("");
    const { tabLeftParticipant } = useSelector((state) => state["features/participants-pane"]);
    const [totalAttendance, setTotalAttendance] = useState(0);
    const currentRoomId = getCurrentRoomId(APP.store.getState());
    const currentRoom = getBreakoutRooms(APP.store.getState())[currentRoomId];
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const allParticipantIds = useSelector((state) => getSortedParticipantIds(state));
    const muteAllVideo = useCallback(() => dispatch(openDialog(MuteEveryonesVideoDialog)), [dispatch]);
    const { local } = useSelector((state) => state["features/base/participants"]);
    const [dataAttendanceResponse, setDataAttendanceResponse] = useState({});
    const leftParentElRef = useRef(null);
    const fetchApiAttendance = async (roomId) => {
        const response = await apiManagerRoom.getAttendance(`?page=-1&roomId=${roomId}&isModerator=1`);
        setDataAttendanceResponse(response);
        setTotalAttendance(response?.totalParticipants);
    };
    const invitePeople = () => {
        sendAnalytics(createToolbarEvent("invite"));
        dispatch(beginAddPeople());
    };
    const onWindowClickListener = useCallback((e) => {
        if (contextOpen && !findAncestorByClass(e.target, classes.footerMoreContainer)) {
            setContextOpen(false);
        }
    }, [contextOpen]);
    useEffect(() => {
        if (paneOpen && !tabLeftParticipant) {
            fetchApiAttendance(currentRoomId);
        }
    }, [allParticipantIds.length, isLocalModerator, local?.name, paneOpen, tabLeftParticipant]);
    useEffect(() => {
        window.addEventListener("click", onWindowClickListener);
        return () => {
            window.removeEventListener("click", onWindowClickListener);
        };
    }, []);
    const onClosePane = useCallback(() => {
        dispatch(close());
    }, []);
    const onDrawerClose = useCallback(() => {
        setContextOpen(false);
    }, []);
    const onMuteAll = useCallback(() => {
        dispatch(openDialog(MuteEveryoneDialog));
    }, []);
    const onToggleContext = useCallback(() => {
        setContextOpen((open) => !open);
    }, []);
    const changeTabHeader = (value) => {
        dispatch(changeTabParticipant(value));
    };
    if (!paneOpen) {
        return null;
    }
    return (React.createElement(React.Fragment, null, paneOpen && (React.createElement("div", { className: "menuControllerWrapper" },
        React.createElement(HeaderMenuController, { headerTitle: "participantsPane.headings.newParticipantsList", closeDialog: onClosePane }),
        React.createElement(SideBar, { textSideBarLeft: 'participantsPane.title', badgeNumberLeft: sortedParticipantIds?.length + participants?.length, badgeNumberRight: totalAttendance, textSideBarRight: (getRoleParticipant(APP.store.getState(), APP.conference.getMyUserId()) !== ROLE_PARTICIPANT_ROOM.PARTICIPANT) ? 'participantsPane.attendance' : '', openSideBarLeft: tabLeftParticipant, setOpenSideBarLeft: changeTabHeader }),
        tabLeftParticipant && (React.createElement("div", null,
            React.createElement("div", { ref: leftParentElRef, className: cx(classes.container, !showFooter && classes.containerNoFooter) },
                React.createElement(VisitorsList, null),
                React.createElement(LobbyParticipants, null),
                React.createElement(RaisedHandParticipants, { searchString: searchString, setSearchString: setSearchString, leftParentElRef: leftParentElRef }),
                React.createElement(MeetingParticipants, { searchString: searchString, setSearchString: setSearchString, leftParentElRef: leftParentElRef })),
            showFooter && (React.createElement("div", { className: classes.footer },
                React.createElement(Button, { accessibilityLabel: t("participantsPane.actions.newInvite"), labelKey: "participantsPane.actions.newInvite", onClick: invitePeople, type: BUTTON_TYPES.PRIMARY, size: "small", className: classes.textSizeCustom }),
                React.createElement("div", { style: { display: "flex", marginLeft: "6px" } },
                    showMuteAllButton && (React.createElement("div", { style: { marginRight: "8px" } },
                        React.createElement(Button, { accessibilityLabel: t("participantsPane.actions.stopAllMicro"), labelKey: "participantsPane.actions.stopAllMicro", onClick: onMuteAll, size: "small", className: classes.textSizeCustom, type: BUTTON_TYPES.SECONDARY }))),
                    React.createElement(Button, { accessibilityLabel: t("participantsPane.actions.stopAllCamera"), labelKey: "participantsPane.actions.stopAllCamera", onClick: muteAllVideo, size: "small", className: classes.textSizeCustom, type: BUTTON_TYPES.SECONDARY })))))),
        !tabLeftParticipant &&
            React.createElement(Attendance, { currentRoomId: currentRoomId, contentSearch: searchString, dataAttendanceResponse: dataAttendanceResponse, setTotalAttendance: setTotalAttendance })))));
};
export default ParticipantsPane;
